import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { DataModelService } from '../services/data-model.service';
import { InboxNotification } from '../services/definitions.service';
import { MessagingService } from '../services/messaging.service';

@Component({
  selector: 'app-notification-inbox',
  templateUrl: './notification-inbox.component.html',
  styleUrls: ['./notification-inbox.component.css'],
})
export class NotificationInboxComponent implements OnInit, OnDestroy {
  notifications: InboxNotification[] = [];
  selectedNotification: InboxNotification | null = null;
  isLoading = true;

  // fcmMessageId of the notification the user clicked, until auto-selected.
  private pendingFcmId: string | null = null;
  private sub: Subscription;

  constructor(
    private dataModel: DataModelService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private messagingService: MessagingService,
  ) {}

  ngOnInit(): void {
    // When the user taps an OS notification the service worker navigates to
    // /notifications?n=<encoded-payload>.  Decode it, save to Firestore (if not
    // already saved via BACKGROUND_MESSAGE_RECEIVED), then clear the param so
    // a page-refresh doesn't create a duplicate.
    const n = this.route.snapshot.queryParamMap.get('n');
    if (n) {
      try {
        const compact = JSON.parse(decodeURIComponent(n));
        this.pendingFcmId = compact.id || null;
        this.messagingService.processNotificationFromUrl(compact);
      } catch (e) {
        console.error('Failed to parse notification URL param', e);
      }
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {},
        replaceUrl: true,
      });
    }

    const userId = this.authService.userId;
    if (userId) {
      this.sub = this.dataModel
        .subscribeToInboxNotifications(userId)
        .subscribe((notifications) => {
          this.notifications = notifications;
          this.isLoading = false;
          // Auto-open the notification that was clicked once it appears in the list.
          if (this.pendingFcmId && !this.selectedNotification) {
            const match = notifications.find(
              (entry) => entry.fcmMessageId === this.pendingFcmId
            );
            if (match) {
              this.openNotification(match);
              this.pendingFcmId = null;
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  openNotification(notification: InboxNotification) {
    this.selectedNotification = notification;
    if (!notification.read) {
      this.dataModel.markNotificationRead(this.authService.userId, notification.id);
    }
  }

  closeDetail() {
    this.selectedNotification = null;
  }

  markAllRead() {
    const userId = this.authService.userId;
    this.notifications
      .filter((n) => !n.read)
      .forEach((n) => this.dataModel.markNotificationRead(userId, n.id));
  }

  formatTimestamp(notification: InboxNotification): string {
    if (!notification.timestamp) return '';
    const date = notification.timestamp.toDate();
    return date.toLocaleDateString('de-DE') + ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.read).length;
  }
}
